import '@rainbow-me/rainbowkit/styles.css'
import '@/styles/globals.css'
import '@/styles/mobile-performance.css'
import '@/styles/mobile-app/shell.css'
import '@/styles/mobile-app/components.css'
import '@/styles/nutrimatic/device.css'
import '@/styles/nutrimatic/mobile-simple.css'
import '@/styles/swap-price-impact.css'
import '@/styles/nutrimatic/tablet.css'

import { QueryClient } from '@tanstack/react-query'
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client'
import React, { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { WagmiProvider } from 'wagmi'

import { LocaleProvider } from './features/locale/LocaleProvider'
import { MARKET_QUERY_CACHE } from './data/marketApi/cachePolicy'
import {
  createMarketQueryPersister,
  MARKET_QUERY_PERSIST_MAX_AGE_MS,
  shouldPersistMarketQuery,
} from './data/marketApi/queryPersist'

import EnsureWalletChainGuard from './components/EnsureWalletChainGuard'
import DeferredAppUpdaters from './components/DeferredAppUpdaters'
import MobileSwapOutcomeSync from './components/MobileSwapOutcomeSync'
import WalletConnectionKeepAlive from './components/WalletConnectionKeepAlive'
import App from './pages/App'
import { ChainScopeProvider } from './config'
import { MarketApiProvider } from './data/marketApi'
import { NutrimaticMarketProvider } from './features/market/NutrimaticMarketProvider'
import store from './state'
import { pruneStaleWalletConnectSession } from './domain/wallet/walletStorage'
import ThemeProvider, { FixedGlobalStyle, ThemedGlobalStyle } from './theme'
import { RainbowKitAppProvider } from './wallet'
import { wagmiConfig } from './wagmi/config'

if ('ethereum' in window) {
  ;(window.ethereum as any).autoRefreshOnNetworkChange = false
}

pruneStaleWalletConnectSession()

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: MARKET_QUERY_CACHE.defaults.staleTime,
      gcTime: MARKET_QUERY_CACHE.defaults.gcTime,
      retry: MARKET_QUERY_CACHE.defaults.retry,
    },
  },
})

const marketQueryPersister = createMarketQueryPersister()

function Updaters() {
  return <DeferredAppUpdaters />
}

const rootEl = document.getElementById('root')
if (!rootEl) {
  throw new Error('Root element #root not found')
}

createRoot(rootEl).render(
  <StrictMode>
    <FixedGlobalStyle />
    <WagmiProvider config={wagmiConfig}>
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{
          persister: marketQueryPersister,
          maxAge: MARKET_QUERY_PERSIST_MAX_AGE_MS,
          dehydrateOptions: {
            shouldDehydrateQuery: shouldPersistMarketQuery,
          },
        }}
      >
        <MarketApiProvider>
        <NutrimaticMarketProvider>
        <Provider store={store}>
          <RainbowKitAppProvider>
            <ChainScopeProvider>
              <WalletConnectionKeepAlive />
              <EnsureWalletChainGuard />
              <MobileSwapOutcomeSync />
              <Updaters />
              <ThemeProvider>
                <ThemedGlobalStyle />
                <LocaleProvider>
                  <App />
                </LocaleProvider>
              </ThemeProvider>
            </ChainScopeProvider>
          </RainbowKitAppProvider>
        </Provider>
        </NutrimaticMarketProvider>
        </MarketApiProvider>
      </PersistQueryClientProvider>
    </WagmiProvider>
  </StrictMode>,
)
